import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Grid from "@mui/material/Grid";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Chip from "@mui/material/Chip";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import TextField from "@mui/material/TextField";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import agencyOrdersService from "../../../../../api/agencyOrders/AgencyOrdersService";
import { getNoaId } from "../../../../../utils/session.utils";
import { dateFormats } from "../../../../../helpers/convertDate";
import { RejectOrderModal } from "./RejectOrderModal";
import s from "./OrderReview.module.css";

// ========================== | PANTALLA DE REVISIÓN Y APROBACIÓN | ======================================

export const OrderReview = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [stockDetails, setStockDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [approvedAmounts, setApprovedAmounts] = useState({});
  const [approvedPackages, setApprovedPackages] = useState({}); // Para juegos instantáneos
  const [validationErrors, setValidationErrors] = useState({});

  const dates = dateFormats();

  // Section: Functions
  const getOrderData = () => {
    setLoading(true);
    console.log("🔍 Obteniendo datos del pedido ID:", id);
    console.log("📡 Endpoint 1: GET /delivery-service/api/agency-orders/" + id);
    console.log("📡 Endpoint 2: GET /delivery-service/api/agency-orders/" + id + "/stock-details");
    
    Promise.all([
      agencyOrdersService.getOrderById(id),
      agencyOrdersService.getOrderStockDetails(id),
    ])
      .then(([orderData, stockData]) => {
        console.log("✅ Datos del pedido recibidos (completo):", JSON.stringify(orderData, null, 2));
        console.log("✅ Estructura de agencia:", {
          agencyNumber: orderData?.agencyNumber,
          agencyName: orderData?.agencyName,
          agency: orderData?.agency,
          agencyId: orderData?.agencyId,
          agency_id: orderData?.agency_id,
          agency_number: orderData?.agency_number,
          agency_name: orderData?.agency_name
        });
        if (orderData?.agency) {
          console.log("✅ Objeto agency completo:", JSON.stringify(orderData.agency, null, 2));
        }
        console.log("✅ Stock details recibidos:", stockData);
        setOrder(orderData);
        setStockDetails(stockData);
        
        // Pre-llenar cantidades aprobadas con el stock disponible o lo solicitado (lo que sea menor)
        const initialAmounts = {};
        const initialPackages = {};
        const items = stockData?.stockDetails || stockData?.items || [];
        items.forEach((item) => {
          const detailId = item.detailId || item.id;
          const canApprove = item.canApproveAmount || item.stockAvailable || 0;
          const requested = item.requestedAmount || item.quantityRequested || 0;
          let amount = Math.min(canApprove, requested);
          
          // Si es instantáneo, trabajar con paquetes
          const isInstant = item.isInstantaneo || item.isInstant || false;
          const couponsPerPackage = item.couponsPerPackage || item.couponsAmountPerBundle || 0;
          if (isInstant && couponsPerPackage > 0) {
            const packages = Math.floor(amount / couponsPerPackage);
            initialPackages[detailId] = packages;
            amount = packages * couponsPerPackage;
          }
          
          initialAmounts[detailId] = amount;
        });
        setApprovedAmounts(initialAmounts);
        setApprovedPackages(initialPackages);
        setValidationErrors({});
      })
      .catch((error) => {
        console.error("❌ Error al obtener datos del pedido:", error);
        console.error("❌ Error response:", error.response);
        console.error("❌ Error URL:", error.config?.url);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // Section: Effects
  useEffect(() => {
    if (id) {
      getOrderData();
    }
  }, [id]);

  // Section: Handlers
  const handleAmountChange = (detailId, newAmount) => {
    const items = stockDetails?.stockDetails || stockDetails?.items || [];
    const item = items.find((i) => (i.detailId || i.id) === detailId);
    if (!item) return;

    const maxAmount = item.canApproveAmount || item.stockAvailable || 0;
    let amount = Math.min(Math.max(0, parseInt(newAmount) || 0), maxAmount);
    
    const errors = { ...validationErrors };
    delete errors[detailId];
    setValidationErrors(errors);
    
    setApprovedAmounts((prev) => ({
      ...prev,
      [detailId]: amount,
    }));
  };

  const handlePackageChange = (detailId, newPackages) => {
    const items = stockDetails?.stockDetails || stockDetails?.items || [];
    const item = items.find((i) => (i.detailId || i.id) === detailId);
    if (!item) return;

    const isInstant = item.isInstantaneo || item.isInstant || false;
    const couponsPerPackage = item.couponsPerPackage || item.couponsAmountPerBundle || 0;
    const maxAmount = item.canApproveAmount || item.stockAvailable || 0;
    const maxPackages = couponsPerPackage > 0 ? Math.floor(maxAmount / couponsPerPackage) : 0;
    
    const packages = Math.min(Math.max(0, parseInt(newPackages) || 0), maxPackages);
    const amount = packages * couponsPerPackage;
    
    const errors = { ...validationErrors };
    delete errors[detailId];
    setValidationErrors(errors);
    
    setApprovedPackages((prev) => ({
      ...prev,
      [detailId]: packages,
    }));
    
    setApprovedAmounts((prev) => ({
      ...prev,
      [detailId]: amount,
    }));
  };

  const handleApproveAll = () => {
    const newAmounts = {};
    const newPackages = {};
    const items = stockDetails?.stockDetails || stockDetails?.items || [];
    items.forEach((item) => {
      const detailId = item.detailId || item.id;
      const canApprove = item.canApproveAmount || item.stockAvailable || 0;
      const requested = item.requestedAmount || item.quantityRequested || 0;
      let amount = Math.min(canApprove, requested);
      
      // Si es instantáneo, trabajar con paquetes
      const isInstant = item.isInstantaneo || item.isInstant || false;
      const couponsPerPackage = item.couponsPerPackage || item.couponsAmountPerBundle || 0;
      if (isInstant && couponsPerPackage > 0) {
        const packages = Math.floor(amount / couponsPerPackage);
        newPackages[detailId] = packages;
        amount = packages * couponsPerPackage;
      }
      
      newAmounts[detailId] = amount;
    });
    setApprovedAmounts(newAmounts);
    setApprovedPackages(newPackages);
    setValidationErrors({});
  };

  const handleApprove = () => {
    const loggedUser = JSON.parse(localStorage.getItem("logged_user") || "{}");
    const approvedBy = loggedUser.email || loggedUser.username || "sistema";
    
    const items = stockDetails?.stockDetails || stockDetails?.items || [];
    
    // Validar que no haya errores de validación
    if (Object.keys(validationErrors).length > 0) {
      alert("Hay errores de validación. Por favor, corrija las cantidades antes de aprobar.");
      return;
    }
    
    // 🔍 LOG: Verificar approvedAmounts antes de procesar
    console.log("🔍 approvedAmounts antes de procesar:", approvedAmounts);
    console.log("🔍 stockDetails:", stockDetails);
    
    const detailApprovals = Object.entries(approvedAmounts)
      .filter(([_, amount]) => amount > 0)
      .map(([detailId, quantityApproved]) => {
        // Validar que detailId sea un número válido
        const parsedDetailId = parseInt(detailId);
        if (isNaN(parsedDetailId) || parsedDetailId <= 0) {
          console.error("❌ detailId inválido:", detailId, "tipo:", typeof detailId);
          return null;
        }
        
        // Validar que quantityApproved sea un número válido y positivo
        const parsedQuantity = parseInt(quantityApproved);
        if (isNaN(parsedQuantity) || parsedQuantity <= 0) {
          console.error("❌ quantityApproved inválido:", quantityApproved, "tipo:", typeof quantityApproved);
          return null;
        }
        
        return {
          detailId: parsedDetailId,
          quantityApproved: parsedQuantity,
          observations: null,
        };
      })
      .filter(item => item !== null); // Filtrar items inválidos

    if (detailApprovals.length === 0) {
      alert("Debe aprobar al menos un detalle con cantidad válida");
      return;
    }

    const payload = {
      detailApprovals: detailApprovals,
      approvedBy: approvedBy,
      observations: null,
    };

    // 🔍 LOGS DETALLADOS PARA DEBUGGING
    console.log("🔍 ========== PAYLOAD DE APROBACIÓN ==========");
    console.log("🔍 Order ID:", id);
    console.log("🔍 URL completa:", `/delivery-service/api/agency-orders/${id}/approve`);
    console.log("🔍 Payload completo:", JSON.stringify(payload, null, 2));
    console.log("🔍 approvedBy:", approvedBy);
    console.log("🔍 detailApprovals count:", detailApprovals.length);
    detailApprovals.forEach((approval, index) => {
      console.log(`🔍   Approval ${index + 1}:`, approval);
    });
    console.log("🔍 ===========================================");

    agencyOrdersService
      .approveOrder(id, payload)
      .then((response) => {
        console.log("✅ ========== RESPUESTA DEL BACKEND AL APROBAR ==========");
        console.log("✅ Respuesta completa:", JSON.stringify(response, null, 2));
        console.log("✅ deliveryNoteId:", response.deliveryNoteId);
        console.log("✅ order:", response);
        console.log("✅ Estructura del remito en la respuesta:", {
          deliveryNoteId: response.deliveryNoteId,
          deliveryNote: response.deliveryNote,
          remito: response.remito,
          deliveryNoteNumber: response.deliveryNoteNumber,
          nroOutput: response.nroOutput,
          date: response.date,
          deliveryDate: response.deliveryDate,
          approvalDate: response.approvalDate,
          agency: response.agency,
          agencyId: response.agencyId,
          agencyNumber: response.agencyNumber,
          agencyName: response.agencyName,
        });
        console.log("✅ ======================================================");
        navigate(`/main/stock-de-cupones/pedidos-agencias/aprobado/${id}`, {
          state: {
            order: response,
            deliveryNoteId: response.deliveryNoteId,
          },
        });
      })
      .catch((error) => {
        console.error("❌ ========== ERROR AL APROBAR PEDIDO ==========");
        console.error("❌ Error completo:", error);
        console.error("❌ Error message:", error.message);
        console.error("❌ Error response:", error.response);
        console.error("❌ Error response data:", error.response?.data);
        console.error("❌ Error response status:", error.response?.status);
        console.error("❌ Error response headers:", error.response?.headers);
        console.error("❌ Payload que se envió:", JSON.stringify(payload, null, 2));
        console.error("❌ Order ID:", id);
        console.error("❌ URL intentada:", error.config?.url);
        console.error("❌ ============================================");
        
        // Extraer mensaje de error del backend
        let errorMessage = "Error desconocido al aprobar el pedido";
        
        if (error.response?.data) {
          const errorData = error.response.data;
          // Intentar extraer el mensaje más descriptivo
          errorMessage = errorData.title 
            || errorData.message 
            || errorData.error 
            || errorData.debugMessage?.substring(0, 200) // Limitar longitud
            || JSON.stringify(errorData).substring(0, 200);
        } else if (error.message) {
          errorMessage = error.message;
        }
        
        // Mostrar mensaje más descriptivo
        const fullErrorMessage = error.response?.data?.title === "Transaction silently rolled back because it has been marked as rollback-only"
          ? "Error en el servidor: La transacción fue revertida. Esto puede deberse a validaciones de negocio o problemas con el stock. Por favor, verifique los datos e intente nuevamente."
          : `Error al aprobar el pedido: ${errorMessage}`;
        
        alert(fullErrorMessage);
      });
  };

  const handleReject = () => {
    setRejectModalOpen(true);
  };

  const handleConfirmReject = (rejectionReason) => {
    const loggedUser = JSON.parse(localStorage.getItem("logged_user") || "{}");
    const rejectedBy = loggedUser.email || loggedUser.username || "sistema";
    
    const payload = {
      rejectionReason: rejectionReason,
      rejectedBy: rejectedBy,
    };
    
    agencyOrdersService
      .rejectOrder(id, payload)
      .then(() => {
        navigate("/main/stock-de-cupones?tab=1");
      })
      .catch((error) => {
        console.error("Error al rechazar pedido:", error);
        alert("Error al rechazar el pedido. Intente nuevamente.");
      });
  };

  const translateStatus = (status) => {
    switch (status) {
      case "PENDING":
        return "Pendiente";
      case "APPROVED":
        return "Aprobado";
      case "REJECTED":
        return "Rechazado";
      default:
        return status || "Pendiente";
    }
  };

  if (loading && !order) {
    return (
      <Grid container className={s.container}>
        <Grid item xs={12} className={s.loader}>
          <CircularProgress size="4rem" />
          <Typography sx={{ mt: 2 }}>Cargando información del pedido...</Typography>
        </Grid>
      </Grid>
    );
  }

  if (!order) {
    return (
      <Grid container className={s.container}>
        <Grid item xs={12} sx={{ textAlign: "center", mt: 4 }}>
          <Typography variant="h6" color="error">
            No se pudo cargar la información del pedido
          </Typography>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/main/stock-de-cupones?tab=1")}
            sx={{ mt: 2 }}
          >
            Volver al listado
          </Button>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container className={s.container}>
      {/* Header */}
      <Grid item xs={12} className={s.header}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/main/stock-de-cupones?tab=1")}
          sx={{ mr: 2 }}
        >
          {t("back") || "Volver"}
        </Button>
        <Typography variant="h4" component="h1">
          Pedido #{order.nroOrder || order.orderId}
        </Typography>
      </Grid>

      {/* Información del Pedido */}
      <Grid item xs={12}>
        <Card className={s.infoCard}>
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" color="text.secondary">
                  Agencia:
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {(() => {
                    const agencyNumber = order.agencyNumber || order.agency?.number || order.agency_number || null;
                    const agencyName = order.agencyName || order.agency?.name || order.agency_name || null;
                    
                    if (agencyNumber && agencyName) {
                      return `${agencyNumber} - ${agencyName}`;
                    } else if (agencyNumber) {
                      return `N° ${agencyNumber}`;
                    } else if (agencyName) {
                      return agencyName;
                    } else if (order.agency?.id) {
                      return `ID: ${order.agency.id}`;
                    } else {
                      return "No disponible";
                    }
                  })()}
                </Typography>
                {(order.agency?.id || order.agencyId || order.agency_id) && (
                  <Typography variant="caption" color="text.secondary">
                    ID: {order.agency?.id || order.agencyId || order.agency_id}
                  </Typography>
                )}
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" color="text.secondary">
                  Fecha:
                </Typography>
                <Typography variant="body1">
                  {dates.formatDateTimeArgentina(order.requestDate || order.createdDate)}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" color="text.secondary">
                  Estado:
                </Typography>
                <Chip
                  label={translateStatus(order.status)}
                  color={order.status === "PENDING" || order.status === "Pendiente" ? "warning" : "default"}
                  size="small"
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      {/* Tabla de Detalles */}
      <Grid item xs={12}>
        <Card className={s.detailsCard}>
          <CardContent>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
              <Typography variant="h6">
                Detalles del Pedido
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={handleApproveAll}
              >
                Aprobar Todo Disponible
              </Button>
            </Box>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ backgroundColor: "primary.main", color: "white" }}>
                      Juego
                    </TableCell>
                    <TableCell sx={{ backgroundColor: "primary.main", color: "white" }}>
                      Sorteo
                    </TableCell>
                    <TableCell sx={{ backgroundColor: "primary.main", color: "white" }}>
                      Solicitado
                    </TableCell>
                    <TableCell sx={{ backgroundColor: "primary.main", color: "white" }}>
                      Stock Disponible
                    </TableCell>
                    <TableCell sx={{ backgroundColor: "primary.main", color: "white" }}>
                      Cantidad a Aprobar
                    </TableCell>
                    <TableCell sx={{ backgroundColor: "primary.main", color: "white" }}>
                      Estado
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(() => {
                    const items = stockDetails?.stockDetails || stockDetails?.items || [];
                    console.log("📊 Items para mostrar:", items);
                    
                    if (items.length === 0) {
                      return (
                        <TableRow>
                          <TableCell colSpan={6} align="center">
                            No hay detalles disponibles
                          </TableCell>
                        </TableRow>
                      );
                    }
                    
                    return items.map((item, index) => {
                      const detailId = item.detailId || item.id;
                      const requested = item.requestedAmount || item.quantityRequested || 0;
                      const available = item.availableStock || item.stockAvailable || 0;
                      const canApprove = item.canApproveAmount || available || 0;
                      const approved = approvedAmounts[detailId] || 0;
                      const isInsufficient = canApprove < requested;
                      const gameName = item.gameName || item.game || "-";
                      const raffleNumber = item.raffleNumber || item.raffle || "-";
                      const isInstant = item.isInstantaneo || item.isInstant || false;
                      const couponsPerPackage = item.couponsPerPackage || item.couponsAmountPerBundle || 0;
                      const maxPackages = isInstant && couponsPerPackage > 0 ? Math.floor(canApprove / couponsPerPackage) : 0;
                      const approvedPackagesCount = approvedPackages[detailId] || 0;
                      const hasError = validationErrors[detailId] || approved > canApprove;

                      return (
                        <TableRow key={index} hover>
                          <TableCell>{gameName}</TableCell>
                          <TableCell>{raffleNumber !== "-" ? `#${raffleNumber}` : "-"}</TableCell>
                          <TableCell>{requested.toLocaleString()}</TableCell>
                          <TableCell>
                            {available.toLocaleString()}
                            {isInsufficient && (
                              <Chip
                                label="⚠️ Insuficiente"
                                color="warning"
                                size="small"
                                sx={{ ml: 1 }}
                              />
                            )}
                          </TableCell>
                          <TableCell>
                            {isInstant && couponsPerPackage > 0 ? (
                              <Box>
                                <TextField
                                  type="number"
                                  size="small"
                                  value={approvedPackagesCount}
                                  onChange={(e) => handlePackageChange(detailId, e.target.value)}
                                  inputProps={{
                                    min: 0,
                                    max: maxPackages,
                                    style: { width: "100px", textAlign: "center" },
                                  }}
                                  error={hasError}
                                  helperText={
                                    approved > canApprove 
                                      ? "Excede stock disponible"
                                      : approvedPackagesCount > 0
                                      ? `${approvedPackagesCount} paquete${approvedPackagesCount !== 1 ? 's' : ''} = ${approved} cupones`
                                      : ""
                                  }
                                  placeholder="0"
                                />
                                <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                                  {couponsPerPackage} cupones por paquete
                                </Typography>
                              </Box>
                            ) : (
                              <TextField
                                type="number"
                                size="small"
                                value={approved}
                                onChange={(e) => handleAmountChange(detailId, e.target.value)}
                                inputProps={{
                                  min: 0,
                                  max: canApprove,
                                  style: { width: "120px", textAlign: "center" },
                                }}
                                error={hasError}
                                helperText={approved > canApprove ? "Excede stock disponible" : ""}
                              />
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={item.stockStatus || (available > 0 ? "Disponible" : "Sin Stock")}
                              color={
                                item.stockStatus === "Crítico" || available === 0
                                  ? "error"
                                  : item.stockStatus === "Insuficiente" || isInsufficient
                                  ? "warning"
                                  : "success"
                              }
                              size="small"
                            />
                          </TableCell>
                        </TableRow>
                      );
                    });
                  })()}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Grid>

      {/* Botones de Acción */}
      <Grid item xs={12} className={s.actionsContainer}>
        <Button
          variant="contained"
          color="success"
          size="large"
          startIcon={<CheckCircleIcon />}
          onClick={handleApprove}
          disabled={loading || Object.values(approvedAmounts).every((amt) => amt === 0)}
        >
          Aprobar Pedido
        </Button>
        <Button
          variant="contained"
          color="error"
          size="large"
          startIcon={<CancelIcon />}
          onClick={handleReject}
          disabled={loading}
        >
          Rechazar Pedido
        </Button>
      </Grid>

      {/* Modal de Rechazo */}
      <RejectOrderModal
        open={rejectModalOpen}
        onClose={() => setRejectModalOpen(false)}
        onConfirm={handleConfirmReject}
        orderNumber={order.nroOrder || order.orderId}
      />
    </Grid>
  );
};


